import Select, { SingleValue } from "react-select";
import { monthOfYear } from "../../../../enums/months";
import { SelectChangeEvent } from "@mui/material/Select";
import { weeklyDays } from "../../../../enums/weeklyDays";
import { useUserStore } from "../../../../store/useUserStore";
import { TicketTypeInputs } from "../../../../enums/ticketType";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { useDarkModeStore } from "../../../../store/useDarkModeStore";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { TicketInterface } from "../../../../models/TicketModule/Ticket";
import React, { useEffect, useState, useMemo, ChangeEvent } from "react";
import { TicketFinancialType } from "../../../../enums/ticketFinancialType";
import { BootstrapInput, StyledSelect } from "../../../../common/GlobalFunctions";
import { ticketRecurringOptions, TicketRecurringPeriod } from "../../../../enums/ticketRecurringPeriod";
import {
  CACHE_KEY_TICKETFINANTIALTYPE_BYFIRSTLASTNAME,
  CACHE_KEY_USERS_BYFIRSTLASTNAME,
  darkModeBackgroudInputs,
} from "../../../../utils/constants";
import dayjs from "dayjs";
import Skeleton from "react-loading-skeleton";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import PhotoComponent from "./PhotoComponent";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import ToastMessage from "../../../../common/ToastMessage";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextAreaCustom from "../../../../common/TextAreaCustom";
import useStatuses from "../../../../store/ReactQueries/statuses/useStatus";
import useCategory from "../../../../store/ReactQueries/categories/useCategory";
import usePriorities from "../../../../store/ReactQueries/priorities/usePriority";
import useCompany from "../../../../store/ReactQueries/general/company/useCompany";
import useDepartment from "../../../../store/ReactQueries/departments/useDepartment";
import useContractors from "../../../../store/ReactQueries/contractors/useContractor";
import useCustomerByCompId from "../../../../store/ReactQueries/customers/useCustomerByCompId";
import useLocationsByCompId from "../../../../store/ReactQueries/locations/useLocationByCompId";
import useUserByFirstLastname from "../../../../store/ReactQueries/users/useUserByFirstLastname";
import useInterventionTypes from "../../../../store/ReactQueries/interventionTypes/useInterventionType";
import useAssetByTitleCompLocCustomer from "../../../../store/ReactQueries/assets/asset/useAssetByTitleCompLocCustomer";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";

interface CustomizedInputsStyledProps {
  name: string;
  label: string;
  view: boolean;
  loading: boolean;
  errors: FieldErrors;
  form: TicketInterface;
  type: TicketTypeInputs;
  isSubTicket?: boolean;
  hasMasterTicketId?: boolean;
  setForm: React.Dispatch<React.SetStateAction<TicketInterface>>;
}

type OptionType = {
  label: any;
  value: string;
};

type HtmlEvent = HTMLInputElement | HTMLTextAreaElement;

const daysOfMonth = Array.from({ length: 28 }, (_, i) => ({ id: (i + 1).toString(), title: i + 1 }));
const hoursOfDay = Array.from({ length: 24 }, (_, i) => ({
  id: i.toString(),
  title: i < 10 ? `0${i}:00h` : `${i}:00h`,
}));

const CustomizedInputsStyled = ({
  type,
  name,
  form,
  view,
  label,
  errors,
  loading,
  isSubTicket = false,
  hasMasterTicketId = false,
  setForm,
}: CustomizedInputsStyledProps) => {
  const { user } = useUserStore();
  const { language } = useLanguageStore();
  const { darkMode } = useDarkModeStore();
  const { Billable, Nonbillable } = TicketFinancialType;
  const { Daily, Weekly, Monthly } = TicketRecurringPeriod;

  const isEmployeeRole = user?.["http://schemas.microsoft.com/ws/2008/06/identity/claims/role"] === "Employee";
  const isAddForm = form.id === "" || form.id === undefined || form.id === null;

  const [assetTitle, setAssetTitle] = useState<string>(form.asset ? form.asset.title : "");
  const [debouncedAssetTitle, setDebouncedAssetTitle] = useState<string>(form.asset ? form.asset.title : "");

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedAssetTitle(assetTitle), 300);
    return () => clearTimeout(timer);
  }, [assetTitle]);
  const [userTitle, setUserTitle] = useState<string>(form.assignedTo ? form.assignedTo.firstname : "");
  const [ticFinTitle, setTicFinTitle] = useState<string>(
    form.financialAssignedTo ? form.financialAssignedTo.firstname : ""
  );

  const { data: statuses, error: statusesError } = useStatuses(0, 0);
  const { data: companies, error: companiesError } = useCompany(0, 0);
  const { data: categories, error: categoriesError } = useCategory(0, 0);
  const { data: priorities, error: prioritiesError } = usePriorities(0, 0);
  const { data: departments, error: departmentError } = useDepartment(0, 0);
  const { data: contractors, error: contractorsError } = useContractors(0, 0);
  const { data: customers, error: customersError } = useCustomerByCompId(0, 0, form.companyId);
  const { data: locations, error: locationsError } = useLocationsByCompId(0, 0, form.companyId);
  const { data: interventionTypes, error: interventionTypesError } = useInterventionTypes(0, 0);
  const { data: users, error: usersError } = useUserByFirstLastname(CACHE_KEY_USERS_BYFIRSTLASTNAME, 1, 20, userTitle);
  const { data: ticFinType, error: ticFinError } = useUserByFirstLastname(
    CACHE_KEY_TICKETFINANTIALTYPE_BYFIRSTLASTNAME,
    1,
    20,
    ticFinTitle
  );

  const { data: assets, error: assetsError } = useAssetByTitleCompLocCustomer(
    1,
    20,
    debouncedAssetTitle,
    form.companyId,
    form.locationId,
    form.customerId
  );

  const handleText = (e: ChangeEvent<HtmlEvent>) => {
    const { name, value } = e.target;
    if (errors) delete errors?.[name];
    setForm({ ...form, [name]: value });
  };

  const handleDate = (e: ChangeEvent<HtmlEvent>) => {
    const { name, value } = e.target;
    delete errors?.[name];
    const res = value ? dayjs(value).format("YYYY-MM-DD") : undefined;
    setForm({ ...form, [name]: res });
  };

  const handleSelect = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target as { name: string; value: string };
    delete errors?.[name];
    if (name === "ticketFinancialType") delete errors?.["financialAssignedToId"];
    const upForm = { ...form };
    upForm[name] = value;
    if (name === "ticketFinancialType") {
      upForm.financialAssignedTo = null;
      upForm.financialAssignedToId = null;
    }
    setForm(upForm);
  };

  const handleStatus = (e: SelectChangeEvent<unknown>) => {
    const { name, value } = e.target as { name: string; value: string };
    const selectedItem = statuses?.find((item) => item.id === value);

    if (selectedItem) {
      delete errors?.[name];
      setForm({
        ...form,
        [name]: value,
        [name.slice(0, -2)]: { ...selectedItem },
      });
    }
  };

  const renderData: any[] = useMemo(() => {
    switch (name) {
      case "statusId":
        return statuses ?? [];
      case "interventionTypeId":
        return interventionTypes ?? [];
      case "locationId":
        return locations ?? [];
      case "priorityId":
        return priorities ?? [];
      case "customerId":
        return customers ?? [];
      case "contractorId":
        return contractors ?? [];
      case "categoryId":
        return categories ?? [];
      case "companyId":
        return companies ?? [];
      case "departmentId":
        return departments ?? [];
      case "recurringPeriod":
        return ticketRecurringOptions.map(({ id, title }) => ({ id, title: language[title] ?? title }));
      case "recurringStartTime":
        if (form.isRecurring) {
          if (form.recurringPeriod === Daily) return hoursOfDay;
          if (form.recurringPeriod === Weekly)
            return weeklyDays.map(({ id, title }) => ({ id, title: language[title] ?? title }));
          if (form.recurringPeriod === Monthly) return daysOfMonth;
          return [];
        }
        return [];
      default:
        return [];
    }
  }, [
    name,
    statuses,
    locations,
    customers,
    companies,
    priorities,
    categories,
    hoursOfDay,
    weeklyDays,
    monthOfYear,
    contractors,
    departments,
    interventionTypes,
    ticketRecurringOptions,
    form.recurringPeriod,
  ]);

  const handleAssignTo = (selectedOption: SingleValue<OptionType>) => {
    if (name === "financialAssignedToId") delete errors?.["financialAssignedToId"];
    if (!selectedOption) {
      setForm({
        ...form,
        [name]: "",
        [name.slice(0, -2)]: { firstname: "", lastname: "" },
      });
      return;
    }

    const fullName = selectedOption.label;
    const [firstname, lastname] = fullName.split(" ");

    setForm({
      ...form,
      [name]: selectedOption.value,
      [name.slice(0, -2)]: { firstname, lastname },
    });
  };

  const handleMultiSelect = (selectedOption: SingleValue<OptionType>) => {
    delete errors?.[name];
    if (name.length > 2) delete errors?.[name.slice(0, -2)];
    let { ...upForm } = form;

    if (name === "companyId") {
      upForm["locationId"] = "";
      upForm["customerId"] = "";
      upForm["assetId"] = "";
      upForm["asset"] = undefined;
      setAssetTitle("");
      setDebouncedAssetTitle("");
    } else if (name === "locationId") {
      upForm["assetId"] = "";
      upForm["asset"] = undefined;
      setAssetTitle("");
      setDebouncedAssetTitle("");
    } else if (name === "customerId") {
      upForm["assetId"] = "";
      upForm["asset"] = undefined;
      setAssetTitle("");
      setDebouncedAssetTitle("");
    }
    if (name === "recurringPeriod") {
      upForm["recurringStartTime"] = undefined;
    }

    if (!selectedOption) {
      upForm[name] = "";
      upForm[name.slice(0, -2)] = { title: "" };

      setForm(upForm);
      return;
    }
    upForm[name] = selectedOption.value;
    upForm[name.slice(0, -2)] = { title: selectedOption.label };
    setForm(upForm);
  };

  const handleAsset = (selectedOption: SingleValue<OptionType>) => {
    if (!selectedOption) {
      setForm({
        ...form,
        [name]: "",
        [name.slice(0, -2)]: { title: "" },
      });
      return;
    }

    setForm({
      ...form,
      [name]: selectedOption.value,
      [name.slice(0, -2)]: { title: selectedOption.label },
    });
  };

  const handleIsRecurring = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.checked;
    let upForm = { ...form, [name]: value };
    if (name === "isRecurring") {
      upForm.recurringPeriod = undefined;
      upForm.recurringStartTime = undefined;
    }
    setForm(upForm);
  };

  const viewPhoto = view && label === "UploadPhoto" && form?.images?.length === 0;
  const viewFinancialAssignedTo =
    (form?.ticketFinancialType === Nonbillable || form?.ticketFinancialType === undefined) &&
    name === "financialAssignedToId";
  const viewLabel = viewPhoto || viewFinancialAssignedTo;

  const getErrorStyle = (errors: FieldErrors, name: string) => {
    return errors?.[name] ? { border: "1px solid #DC3545", borderRadius: "4px" } : {};
  };

  const renderInput = () => {
    switch (type) {
      case "text":
        return (
          <BootstrapInput
            id={name}
            name={name}
            readOnly={view}
            onChange={handleText}
            value={form?.[name] ?? ""}
            sx={getErrorStyle(errors, name)}
            darkmode={darkMode ? darkMode.toString() : "false"}
          />
        );
      case "phone": {
        if (name === "phoneNo" && !view) return null;

        return (
          <div
            className={`mt-4 phone-field ${darkMode ? "dark-mode-input" : ""} ${errors?.[name] ? "has-error" : ""}`}
            style={view ? { pointerEvents: "none" } : undefined}
          >
            <PhoneInput
              international
              defaultCountry="XK"
              value={name === "phoneNo" ? form["customer"]?.phoneNo ?? "" : form?.[name] ?? ""}
              onChange={(val) => {
                if (!view) setForm((p: any) => ({ ...p, [name]: val || "" }));
              }}
              placeholder="+383"
            />
          </div>
        );
      }
      case "multitext":
        return (
          <TextAreaCustom form={form} name={name} view={view} errors={errors} className="mt-4" setForm={setForm} />
        );
      case "ticketFinancialType":
        return (
          <StyledSelect
            name={name}
            value={form?.[name]}
            onChange={handleSelect}
            darkmode={darkMode.toString()}
            sx={getErrorStyle(errors, name)}
            readOnly={view || isEmployeeRole}
            className={`${darkMode && "dark-mode-input"}`}
            inputProps={{ "aria-label": "Without label", name }}
          >
            <MenuItem key={Nonbillable} value={Nonbillable}>
              {TicketFinancialType[Nonbillable]}
            </MenuItem>
            <MenuItem key={Billable} value={Billable}>
              {TicketFinancialType[Billable]}
            </MenuItem>
          </StyledSelect>
        );
      case "assignedToId":
        return (
          <Select
            value={{
              label: `${form?.assignedTo?.firstname ?? ""} ${form?.assignedTo?.lastname ?? ""}`,
              value: form?.assignedToId || "",
            }}
            className={`mt-4 ${darkMode && "dark-mode-input"}`}
            onChange={handleAssignTo}
            onInputChange={(inputValue) => setUserTitle(inputValue)}
            placeholder="Zgjedh"
            options={users
              ?.filter((user) => user.isActive)
              .map(({ id, firstname, lastname }) => ({
                value: id ?? "",
                label: `${firstname ?? ""} ${lastname ?? ""}`,
              }))}
            isClearable
            isDisabled={view || isEmployeeRole}
            styles={{
              menu: (provided) => ({
                ...provided,
                zIndex: 11,
              }),
              container: (provided) => ({
                ...provided,
                zIndex: 11,
                position: "relative",
              }),
              singleValue: (provided) => ({
                ...provided,
                color: darkMode ? "white" : "black",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 11 }),
              control: (provided) => ({
                ...provided,
                borderColor: darkMode ? darkModeBackgroudInputs : "initial",
                backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
              }),
            }}
            menuPlacement="top"
            menuPortalTarget={document.body}
          />
        );
      case "statusId": {
        return (
          <StyledSelect
            name={name}
            readOnly={view}
            value={form?.[name]}
            onChange={handleStatus}
            sx={getErrorStyle(errors, name)}
            className={`${darkMode && "dark-mode-input"}`}
            inputProps={{ "aria-label": "Without label", name }}
            darkmode={darkMode ? darkMode.toString() : "false"}
          >
            {renderData?.map((item) => (
              <MenuItem key={item.id} value={item.id}>
                {item.title}
              </MenuItem>
            ))}
          </StyledSelect>
        );
      }
      case "assetId": {
        return (
          <Select
            value={{
              label: `${form?.asset?.title ?? ""}`,
              value: form?.assetId || "",
            }}
            onChange={handleAsset}
            onInputChange={(title) => setAssetTitle(title)}
            placeholder="Zgjedh"
            className="mt-4"
            options={assets?.map(({ id, title, barcode, assetCategory }) => ({
              value: id ?? "",
              label: title ?? "",
              barcode: barcode ?? "",
              category: assetCategory?.title ?? "",
            }))}
            filterOption={() => true}
            formatOptionLabel={(option: any, { context }: any) => {
              if (context === "value") return <span>{option.label}</span>;
              return (
                <div>
                  <div style={{ fontWeight: 500 }}>{option.label}</div>
                  <div style={{ fontSize: "0.75rem", color: "#888", display: "flex", gap: "12px" }}>
                    {option.barcode && <span>Barcode: {option.barcode}</span>}
                    {option.category && <span>{option.category}</span>}
                  </div>
                </div>
              );
            }}
            isClearable
            // isDisabled={view || !form.companyId}
            isDisabled={view}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 11 }),
              container: (provided) => ({
                ...provided,
                zIndex: 11,
                position: "relative",
              }),
              singleValue: (provided) => ({
                ...provided,
                color: darkMode ? "white" : "black",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 11 }),
              control: (provided) => ({
                ...provided,
                borderColor: darkMode ? darkModeBackgroudInputs : "initial",
                backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
              }),
            }}
            menuPlacement="top"
            menuPortalTarget={document.body}
          />
        );
      }
      case "select":
        if (["recurringPeriod", "recurringStartTime"].includes(name) && !isAddForm && form?.isMasterTicket) return null;
        return (
          <Select
            value={{ value: form?.[name], label: renderData?.find((item) => item.id === form?.[name])?.title ?? "" }}
            className={`mt-4 ${darkMode && "dark-mode-input"}`}
            onChange={handleMultiSelect}
            placeholder="Zgjedh"
            options={renderData?.map(({ id, title }) => ({
              value: id ?? "",
              label: title ?? "",
            }))}
            isClearable={!["companyId", "recurringPeriod"].includes(name)}
            isDisabled={view}
            styles={{
              menu: (provided) => ({ ...provided, zIndex: 11 }),
              container: (provided) => ({
                ...provided,
                zIndex: 11,
                position: "relative",
              }),
              singleValue: (provided) => ({
                ...provided,
                color: darkMode ? "white" : "black",
              }),
              menuPortal: (base) => ({ ...base, zIndex: 11 }),
              control: (provided) => ({
                ...provided,
                borderColor: darkMode ? darkModeBackgroudInputs : "initial",
                backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
              }),
            }}
            menuPlacement="top"
            menuPortalTarget={document.body}
          />
        );
      case "date":
        return (
          <BootstrapInput
            darkmode={darkMode ? darkMode.toString() : "false"}
            value={form?.[name] ? dayjs(form[name]).format("YYYY-MM-DD") : form?.[name] ?? ""}
            id={name}
            name={name}
            type="date"
            sx={getErrorStyle(errors, name)}
            onChange={handleDate}
            readOnly={view}
          />
        );
      case "checkbox":
        if (name === "isMasterTicket" && hasMasterTicketId) return null;
        if ((isAddForm || isSubTicket) && name === "isMasterTicket") return null;
        // else if (name === 'isRecurring' && view) return null;
        else if (!isAddForm && name === "isRecurring" && form?.isMasterTicket) return null;
        return (
          <FormControlLabel
            className="mt-4 ms-3"
            control={
              <Checkbox
                id={name}
                name={name}
                onChange={handleIsRecurring}
                sx={getErrorStyle(errors, name)}
                disabled={view}
                className={`${darkMode && "dark-mode-input"} `}
                checked={Boolean(form?.[name] ?? false) ?? false}
              />
            }
            label={language[label] ?? label}
          />
        );
      case "photo":
        return <PhotoComponent form={form} view={view} setForm={setForm} />;
      case "financialAssignedToId": {
        if (name === "financialAssignedToId" && form["ticketFinancialType"] === Billable)
          return (
            <Select
              value={{
                label: `${form?.financialAssignedTo?.firstname ?? ""} ${form?.financialAssignedTo?.lastname ?? ""}`,
                value: form?.financialAssignedToId || "",
              }}
              onChange={handleAssignTo}
              onInputChange={(inputValue) => setTicFinTitle(inputValue)}
              placeholder="Zgjedh"
              className={`mt-4 ${darkMode && "dark-mode-input"}`}
              options={ticFinType?.map(({ id, firstname, lastname }) => ({
                value: id ?? "",
                label: `${firstname ?? ""} ${lastname ?? ""}`,
              }))}
              isClearable
              isDisabled={view || isEmployeeRole}
              styles={{
                menu: (provided) => ({ ...provided, zIndex: 11 }),
                container: (provided) => ({
                  ...provided,
                  zIndex: 11,
                  position: "relative",
                }),
                singleValue: (provided) => ({
                  ...provided,
                  color: darkMode ? "white" : "black",
                }),
                menuPortal: (base) => ({ ...base, zIndex: 11 }),
                control: (provided) => ({
                  ...provided,
                  borderColor: darkMode ? darkModeBackgroudInputs : "initial",
                  backgroundColor: darkMode ? darkModeBackgroudInputs : "#F3F6F9",
                }),
              }}
              menuPlacement="top"
              menuPortalTarget={document.body}
            />
          );
      }
      default:
        return null;
    }
  };

  const handleHiddenInputs = () => {
    const shouldHideRecurringFields = !form.isRecurring || form.isMasterTicket;
    //if the isRecurring is false hide the recurringPeriod
    if (["recurringPeriod", "recurringStartTime"].includes(name) && shouldHideRecurringFields) {
      return true;
    }
  };

  const apiErrors = [
    usersError,
    ticFinError,
    assetsError,
    statusesError,
    locationsError,
    companiesError,
    customersError,
    prioritiesError,
    categoriesError,
    departmentError,
    contractorsError,
    interventionTypesError,
  ];

  useEffect(() => {
    apiErrors.filter(Boolean).forEach((error) => ToastMessage.Failed(`Error: ${error?.message}`));
  }, apiErrors);

  useEffect(() => {
    if (form.assignedTo) setUserTitle(form.assignedTo.firstname);
    if (form.financialAssignedTo) setTicFinTitle(form.financialAssignedTo.firstname);
  }, [form.assignedTo, form.financialAssignedTo]);

  useEffect(() => {
    if (isAddForm && !form.companyId && name !== "locationId" && name !== "customerId") {
      const defaultValues = renderData?.find((i) => i.isDefault)?.id;
      setForm((prevForm) => ({
        ...prevForm,
        [name]: defaultValues,
        ticketFinancialType: Nonbillable,
        isRecurring: false,
      }));
    }
  }, [form.id, renderData, name, setForm]);

  return handleHiddenInputs() ? null : (
    <FormControl variant="standard" className={["multitext", "photo"].includes(type) ? "modalMergeThreeRows" : ""}>
      {type !== "checkbox" && (
        <InputLabel
          shrink
          htmlFor={name}
          sx={errors?.[name] ? { color: "#DC3545" } : { color: darkMode ? "white" : "rgba(0, 0, 0, 0.6)" }}
        >
          {name === "phoneNo" && !view ? null : !viewLabel && (language[label] ?? label)}
        </InputLabel>
      )}
      {loading ? <Skeleton className="mt-4" style={{ width: "100%", height: 41 }} /> : renderInput()}
      {errors?.[name] && (
        <p className="text-danger mb-0" style={{ fontSize: "12px" }}>
          {errors?.[name]}
        </p>
      )}
    </FormControl>
  );
};

export default CustomizedInputsStyled;
